import { CheckCircle, XCircle } from "../../../components/icons/svg/outline";
import { isDayWithinJobActiveRange } from "../../planning/schedule/model/scheduleJobDateRange";
import { getJobActiveStatusLabel } from "../../../utils/business";
import { getTodayInPT, toProjectDateKey } from "../../../utils/datetime";
import { getUserDisplayName } from "../../../utils/business/user";

/**
 * Whether the project/job is date-window active today.
 *
 * Prefers API ``active`` from ``JobNumberSerializer`` /
 * ``is_job_date_window_active``. Falls back to the shared client date-window
 * helper (same rules) when the payload has no ``active`` field.
 *
 * @param {Object|null|undefined} project - Project / JobNumber row.
 * @returns {boolean}
 */
export function isProjectActiveToday(project) {
  if (!project) {
    return false;
  }
  if (typeof project.active === "boolean") {
    return project.active;
  }
  return isDayWithinJobActiveRange(getTodayInPT(), project);
}

/**
 * Return status chip config from date-window active status (Active / Inactive).
 *
 * @param {boolean|Object} isActiveOrProject - Active flag, or project row to evaluate.
 * @param {"home"|"header"} [variant="home"] - Surface-specific chip colors.
 * @returns {{ label: string, Icon: React.ComponentType<{className?: string}>, className: string, isActive: boolean }}
 */
export function getProjectStatusConfig(isActiveOrProject, variant = "home") {
  const isActive =
    typeof isActiveOrProject === "boolean"
      ? isActiveOrProject
      : isProjectActiveToday(isActiveOrProject);
  if (isActive) {
    return {
      label: getJobActiveStatusLabel(true),
      Icon: CheckCircle,
      className:
        variant === "header"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-primary bg-primary text-white",
      isActive: true,
    };
  }
  return {
    label: getJobActiveStatusLabel(false),
    Icon: XCircle,
    className: "border-gray-200 bg-gray-100 text-gray-600",
    isActive: false,
  };
}

/** Shared chip chrome for status + manager pills on Projects Home surfaces. */
export const PROJECT_HOME_CHIP_CLASS =
  "inline-flex h-4 items-center gap-0.5 rounded-full border px-1.5 text-[10px] max-h-[16px] font-normal leading-none";

/** Manager pills — muted gray for the grayscale card chrome. */
export const PROJECT_HOME_MANAGER_CHIP_CLASS =
  "border-gray-200 bg-gray-100 text-gray-700";

/**
 * Format a brief card metric for display (whole number when possible).
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatProjectCardMetric(value) {
  const n = Number(value) || 0;
  if (Number.isInteger(n)) {
    return String(n);
  }
  return n.toFixed(1);
}

/**
 * Empty concrete/labor brief summary for a project card.
 *
 * @returns {{ concreteToday: number, concrete7d: number, laborPayrollWeekHours: number, payrollWeekEnding: string|null }}
 */
export function emptyProjectCardOpsSummary() {
  return {
    concreteToday: 0,
    concrete7d: 0,
    laborPayrollWeekHours: 0,
    payrollWeekEnding: null,
  };
}

/**
 * Normalize a Projects Home ops-summary API row for card display.
 *
 * @param {Object|null|undefined} row - Row from ``GET /api/projects/ops-summaries/``.
 * @param {string|null} [payrollWeekEnding=null] - Fallback week ending from batch payload.
 * @returns {{ concreteToday: number, concrete7d: number, laborPayrollWeekHours: number, payrollWeekEnding: string|null }}
 */
export function normalizeProjectCardOpsSummary(row, payrollWeekEnding = null) {
  return {
    concreteToday: Number(row?.concreteToday) || 0,
    concrete7d: Number(row?.concrete7d) || 0,
    laborPayrollWeekHours: Number(row?.laborPayrollWeekHours) || 0,
    payrollWeekEnding:
      row?.payrollWeekEnding || payrollWeekEnding || null,
  };
}

/**
 * Whether a project matches the Projects Home start/end date filter.
 *
 * - Both empty: inactive (always matches).
 * - Both set: project must have both dates and be fully contained (inclusive).
 * - Start only: ``date_start >= filterStart``; ignore ``date_end``.
 * - End only: ``date_end <= filterEnd``; ignore ``date_start``.
 * - Both set with start > end: no matches.
 *
 * @param {Object} project - Project API row.
 * @param {string} filterStart - Filter start (`YYYY-MM-DD` or empty).
 * @param {string} filterEnd - Filter end (`YYYY-MM-DD` or empty).
 * @returns {boolean}
 */
export function projectMatchesDateRangeFilter(project, filterStart, filterEnd) {
  const hasStart = Boolean(filterStart);
  const hasEnd = Boolean(filterEnd);
  if (!hasStart && !hasEnd) {
    return true;
  }
  if (hasStart && hasEnd && filterStart > filterEnd) {
    return false;
  }

  const projectStart = toProjectDateKey(project?.date_start);
  const projectEnd = toProjectDateKey(project?.date_end);

  if (hasStart && hasEnd) {
    if (!projectStart || !projectEnd) {
      return false;
    }
    return projectStart >= filterStart && projectEnd <= filterEnd;
  }

  if (hasStart) {
    return Boolean(projectStart) && projectStart >= filterStart;
  }

  return Boolean(projectEnd) && projectEnd <= filterEnd;
}

/**
 * Build sorted customer multi-select options from loaded projects.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildProjectCustomerFilterOptions(projects) {
  const byId = new Map();
  for (const project of projects || []) {
    const id = project?.customer?.id;
    const name = project?.customer?.name;
    if (id == null || !name) {
      continue;
    }
    byId.set(String(id), name);
  }
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Build sorted manager multi-select options from loaded projects.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildProjectManagerFilterOptions(projects) {
  const byId = new Map();
  for (const project of projects || []) {
    for (const manager of project?.managers || []) {
      if (manager?.id == null) {
        continue;
      }
      const label = getUserDisplayName(manager, "");
      if (!label) {
        continue;
      }
      byId.set(String(manager.id), label);
    }
  }
  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Whether a project matches the selected customer filter (empty = inactive).
 *
 * @param {Object} project - Project API row.
 * @param {Array<string>} selectedCustomerIds - Selected customer id strings.
 * @returns {boolean}
 */
export function projectMatchesCustomerFilter(project, selectedCustomerIds) {
  if (!selectedCustomerIds?.length) {
    return true;
  }
  const customerId = project?.customer?.id;
  if (customerId == null) {
    return false;
  }
  return selectedCustomerIds.includes(String(customerId));
}

/**
 * Whether a project matches the selected manager filter (OR: any selected manager).
 *
 * @param {Object} project - Project API row.
 * @param {Array<string>} selectedManagerIds - Selected manager id strings.
 * @returns {boolean}
 */
export function projectMatchesManagerFilter(project, selectedManagerIds) {
  if (!selectedManagerIds?.length) {
    return true;
  }
  const managerIds = (project?.managers || [])
    .map((manager) => (manager?.id == null ? null : String(manager.id)))
    .filter(Boolean);
  return selectedManagerIds.some((id) => managerIds.includes(id));
}

/**
 * Whether a project card T&M badge has no counts to show.
 *
 * @param {{open?: number, sent?: number, signed?: number}|null|undefined} summary
 * @returns {boolean}
 */
export function isTmCardSummaryEmpty(summary) {
  if (!summary) {
    return true;
  }
  return (
    !(Number(summary.open) || 0) &&
    !(Number(summary.sent) || 0) &&
    !(Number(summary.signed) || 0)
  );
}

/**
 * Whether a project card RFI badge has no open-pipeline counts to show.
 *
 * Closed RFIs are omitted from the badge, so they do not count as data.
 *
 * @param {{open?: number, reviewing?: number, responding?: number}|null|undefined} summary
 * @returns {boolean}
 */
export function isRfiCardSummaryEmpty(summary) {
  if (!summary) {
    return true;
  }
  return (
    !(Number(summary.open) || 0) &&
    !(Number(summary.reviewing) || 0) &&
    !(Number(summary.responding) || 0)
  );
}
